// components/TimePlotViewer.jsx
import React, { useRef, useEffect, useState, useMemo } from 'react';

// Process cumulative data for the timeline
const processCumulativeData = (table, dateCol, searchIndices, includeMissingDates) => {
  if (!table || !dateCol) return { cumulative: [], missingCount: 0 };
  
  const dateData = table.getChild(dateCol);
  const data = [];
  let missingCount = 0;
  
  const source = searchIndices || Array.from({ length: table.numRows }, (_, i) => i);

  for (let i = 0; i < source.length; i++) {
    const rowIdx = source[i];
    const val = dateData.get(rowIdx);
    const ts = new Date(val).getTime();
    
    // Treat null, NaN, and explicit 0 (1970) as missing
    if (isNaN(ts) || ts === 0) {
      missingCount++;
    } else {
      data.push({ x: ts });
    }
  }
  
  data.sort((a, b) => a.x - b.x);
  
  const cumulative = [];
  // Start the count from the missing count if they are being included
  let currentCount = includeMissingDates ? missingCount : 0;
  
  data.forEach((entry, i) => {
    currentCount++;
    if (i === data.length - 1 || data[i+1].x !== entry.x) {
      cumulative.push({ x: entry.x, y: currentCount });
    }
  });

  return { cumulative, missingCount };
};

const TimePlotViewer = React.memo(function TimePlotViewer({ 
  table,
  dateCol,
  searchIndices,
  minX,
  maxX,
  range,
  onChange,
  isLogScale,
  includeMissingDates = false
}) {
  const containerRef = useRef(null);
  const dragStateRef = useRef(null);

  const [draggingIdx, setDraggingIdx] = useState(null);
  const [isDraggingWindow, setIsDraggingWindow] = useState(false);
  const [hoveredHandle, setHoveredHandle] = useState(null);

  const fmtDate = (val) => {
    const d = new Date(minX + val * (maxX - minX));
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  const { cumulative: plotData, missingCount } = useMemo(() => 
    processCumulativeData(table, dateCol, searchIndices, includeMissingDates), 
  [table, dateCol, searchIndices, includeMissingDates]);
  
  // Total Y is the last cumulative point, or just the missing count if no valid dates exist
  const maxY = plotData.length > 0 ? plotData[plotData.length - 1].y : (includeMissingDates ? missingCount : 1);


  // Coordinate Mapping Helpers (Normalized 0-100 for SVG with preserveAspectRatio="none")
  const getNormX = (timestamp) => ((timestamp - minX) / (maxX - minX)) * 100;
  const getNormY = (count) => {
    if (isLogScale) {
      return 100 - (Math.log10(count + 1) / Math.log10(maxY + 1)) * 100;
    }
    return 100 - (count / maxY) * 100;
  };

  // Handle individual handle resizing/dragging
  const handleMouseDown = (index) => (e) => {
    e.preventDefault();
    setDraggingIdx(index);
    if (dragStateRef.current) return;
    const startX = e.clientX;
    const startVal = range[index];
    let latestRange = range;
    
    const onMouseMove = (moveE) => {
      const rect = containerRef.current.getBoundingClientRect();
      const delta = (moveE.clientX - startX) / rect.width;
      const newRange = [...range];
      newRange[index] = Math.max(0, Math.min(1, startVal + delta));
      latestRange = newRange;
      onChange(newRange, false);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      dragStateRef.current = null;
      setDraggingIdx(null);
      onChange(latestRange, true); 
    };

    dragStateRef.current = { onMouseMove, onMouseUp };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Handle dragging the entire selection window
  const handleWindowMouseDown = (e) => {
    e.preventDefault();
    if (dragStateRef.current) return;
    
    const startX = e.clientX;
    const startRange = [...range];
    const minVal = Math.min(...startRange);
    const maxVal = Math.max(...startRange);
    let latestRange = startRange;
    
    setIsDraggingWindow(true);
    
    const onMouseMove = (moveE) => {
      const rect = containerRef.current.getBoundingClientRect();
      let rawDelta = (moveE.clientX - startX) / rect.width;
      
      // Constrain delta so we don't slide outside of the [0, 1] bounds
      if (minVal + rawDelta < 0) {
        rawDelta = -minVal;
      } else if (maxVal + rawDelta > 1) {
        rawDelta = 1 - maxVal;
      }
      
      latestRange = [startRange[0] + rawDelta, startRange[1] + rawDelta];
      onChange(latestRange, false);
    };
    
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      dragStateRef.current = null;
      setIsDraggingWindow(false);
      onChange(latestRange, true);
    };
    
    dragStateRef.current = { onMouseMove, onMouseUp };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Cleanup mouse events on unmount
  useEffect(() => {
    return () => {
      if (dragStateRef.current) {
        document.removeEventListener('mousemove', dragStateRef.current.onMouseMove);
        document.removeEventListener('mouseup', dragStateRef.current.onMouseUp);
      }
    };
  }, []);

  // Generate Path
  const pathData = useMemo(() => {
    if (plotData.length === 0 && !includeMissingDates) return "";
    
    const startY = includeMissingDates ? missingCount : 0;
    let d = `M ${getNormX(minX)} ${getNormY(startY)}`;
    let lastY = startY;
    let lastX = minX;
          
    // For each data point, draw a horizontal line from the previous point's x to this point's x
    // at the previous count, then a vertical line up to the new count
    plotData.forEach(p => {
      // Horizontal line from last point to this point's x at the previous count
      if (p.x > lastX) d += ` L ${getNormX(p.x)} ${getNormY(lastY)}`;
      // Vertical line up to new count
      d += ` L ${getNormX(p.x)} ${getNormY(p.y)}`;
      lastY = p.y;
      lastX = p.x;
    });
    // If we haven't reached maxX yet, continue the last count to the end
    if (lastX < maxX) d += ` L ${getNormX(maxX)} ${getNormY(lastY)}`;
    
    // Close the path by dropping to the bottom (Y=100)
    d += ` L ${getNormX(maxX)} 100 L ${getNormX(minX)} 100 Z`;
    return d;
  }, [plotData, minX, maxX, isLogScale, includeMissingDates, missingCount]);

  // Number Formatter (e.g., 1500 -> 1.5k)
  const formatY = (val) => {
    if (val >= 1000) return (val / 1000).toFixed(1) + 'k';
    return Math.round(val);
  };

  const levels = [1, 0.75, 0.5, 0.25, 0];

  return (
    <div className="flex flex-col h-full w-full gap-0 pl-4 pr-6 pt-4 pb-4 select-none overflow-hidden">
      <div className="flex flex-row flex-grow min-h-0">
        
        {/* Y-Axis Gutter (Fixed width) */}
        <div className="relative w-10 border-r border-gray-200 text-gray-600 text-[11px]">
          {levels.map(p => (
            <div 
              key={p} 
              className="absolute right-2 -translate-y-1/2 text-right whitespace-nowrap"
              style={{ top: `${(1 - p) * 100}%` }}
            >
              {formatY(isLogScale 
                ? (Math.pow(10, p * Math.log10(maxY + 1)) - 1) 
                : (maxY * p))}
            </div>
          ))}
        </div>

        {/* Chart Area */}
        <div ref={containerRef} className="relative flex-grow bg-white">
          
          {/* Grid Lines */}
          <div className="absolute inset-0 pointer-events-none opacity-30">
            {levels.map(p => (
              <div 
                key={p} 
                className={`absolute w-full ${p!=0? "border-t border-gray-400 border-dashed":"border-t border-blue-400"}`} 
                style={{ top: `${(1 - p) * 100}%` }}
              />
            ))}
          </div>

          <svg 
            className="w-full h-full" 
            viewBox="0 0 100 100" 
            preserveAspectRatio="none"
          >
            {/* Highlighted Selection Area */}
            <rect 
              x={`${Math.min(...range) * 100}`} 
              width={`${Math.abs(range[1] - range[0]) * 100}`}
              y="0" height="100"
              fill="rgba(59, 153, 246, 0.1)" 
            />
            
            <path 
              d={pathData}
              fill="rgba(59, 130, 246, 0.15)"
              stroke="#3b82f6"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          {/* Draggable Window Overlay */}
          <div 
            className="absolute top-0 bottom-0 z-20"
            style={{
              left: `${Math.min(...range) * 100}%`,
              width: `${Math.abs(range[1] - range[0]) * 100}%`,
              cursor: isDraggingWindow ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleWindowMouseDown}
          />

          {/* Handles */}
          {range.map((val, i) => {
          const isActive = draggingIdx === i || hoveredHandle === i;
          return (
            <div 
              key={i}
              onMouseDown={handleMouseDown(i)}
              onMouseEnter={() => setHoveredHandle(i)}
              onMouseLeave={() => setHoveredHandle(null)}
              className="absolute top-0 bottom-0 w-4 -ml-2 cursor-ew-resize z-30"
              style={{ left: `${val * 100}%` }}
            >
              {/* Vertical Blue Line */}
              <div className={`absolute inset-y-0 left-1/2 -translate-x-1/2 w-1 bg-blue-600 transition-opacity ${isActive ? 'opacity-80' : 'opacity-30'}`} />

              {/* Knob */}
              <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-8 bg-white rounded-full border-2 shadow-md ${isActive ? 'border-blue-600' : 'border-blue-500'}`} />

              {/* Tooltip */}
              {false && isActive && (
                <div className="absolute bottom-1/2 mb-8 right-1/2 translate-x-1/2 pointer-events-none z-[100]">
                  <div className="bg-white border border-gray-800 text-[10px] font-bold px-2 py-0.5 rounded-lg text-center whitespace-nowrap shadow-sm">
                    {fmtDate(val)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>

      {/* X-Axis Footer (Fixed height) */}
      <div className="h-5 flex flex-row mt-2">
        <div className="w-10" /> {/* Spacer matching Y gutter width */}
        <div className="flex-grow flex justify-between px-1 text-[11px] text-gray-600">
          <span>{new Date(minX).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
          <span>{new Date(minX + (maxX - minX) * 0.5).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
          <span>{new Date(maxX).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
        </div>
      </div>
    </div>
  );
});

export default TimePlotViewer;